import React, { useState, useEffect } from "react";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import CollapsibleCard from "components/CollapsibleCard";
import { Checkbox } from "@mui/material";
import { useStoreActions, useStoreState } from "easy-peasy";
import { formatCurrencyUSD } from "utils/CommonUtils";
import { MESSAGES } from "constants/Messages";
import { get } from "requests";
import DealPayOffTable from "./DealPayOffTable";
import { useSubmissionStore } from "models/applications/SubmissionStore";
import EditableTable from "lib/Tables/EditableTable";
import { options } from "dropzone";

interface ExternalPayOffsProps {
  dealApplicationId: number;
  setComponentLoading?: (componentName: string, isLoading: boolean) => void;
}

const ExternalPayOffs: React.FC<ExternalPayOffsProps> = ({
  dealApplicationId,
  setComponentLoading,
}) => {
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [externalPayOffData, setExternalPayOffData] = useState<any[]>([]);
  const [externalFunders, setExternalFunders] = useState<any[]>([]);
  const [externalFunderOptions, setExternalFunderOptions] = useState<any[]>([]);

  const offerDetails = useStoreState((state: any) => state.offerStore.contractTerms);
  const contractTerms = offerDetails?.contractTerms;

  const [editingRow, setEditingRow] = useState<any | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [sortedColumn, setSortedColumn] = useState<string | null>("amount");
  const setLoading = useStoreActions((actions: any) => actions.offerStore.setLoading);
  const saveFinalOffer = useStoreActions((actions: any) => actions.offerStore.saveContractTerms);
  const getContractTerms = useStoreActions((actions: any) => actions.offerStore.getContractTerms);
  const offerStoreRefresh = useStoreActions((actions: any) => actions.offerStore.onInit);

  const setSyndicationBankFeeAlert = useStoreActions(
    (actions: any) => actions.heldBackCommissionStore.setSyndicatorBankFeeAlert
  );

  const updateSyndicatorsPercentage = useStoreActions(
    (actions: any) => actions.syndicationStore.updateSyndicatorsPercentage
  );
  const { dealApplicationData, fetchInitialData } = useSubmissionStore();

  useEffect(() => {
    try {
      setComponentLoading("earlyPayoffs", true);
      let payOffData: any = [];

      if (contractTerms?.externalFunderId) {
        payOffData = [
          {
            externalAbaNumber: contractTerms?.externalAbaNumber,
            externalAccountNumber: contractTerms?.externalAccountNumber,
            externalAmount: contractTerms?.externalAmount,
            externalBankName: contractTerms?.externalBankName,
            externalFunderId: contractTerms.externalFunderId,
            mcaCompany: contractTerms?.mcaCompany,
          },
        ];
      }

      fetchExternalFunders();
      setExternalPayOffData(payOffData);
    } finally {
      setComponentLoading("earlyPayoffs", false);
    }
  }, [contractTerms, dealApplicationData]);

  const columns = [
    {
      Header: "MCA Company Name",
      input: "select",
      accessor: "mcaCompany",
      selector: "externalFunderId",
      allowSelection: false,
      minWidth: 200,
      align: "left",
      required: false,
      options: externalFunderOptions,
    },
    {
      Header: "ABA Number",
      accessor: "externalAbaNumber",
      minWidth: 200,
      align: "left",
      disabled: (): boolean => true,
      required: false,
    },
    {
      Header: "Account Number",
      accessor: "externalAccountNumber",
      minWidth: 200,
      align: "left",
      disabled: (): boolean => true,
      required: false,
    },
    {
      Header: "Bank Name",
      accessor: "externalBankName",
      minWidth: 200,
      align: "left",
      disabled: (): boolean => true,
      required: false,
    },
    {
      Header: "Amount",
      accessor: "externalAmount",
      minWidth: 200,
      align: "left",
      input: "amount",
      Cell: (row: any) => formatCurrencyUSD(row?.row?.externalAmount),
    },
  ];

  const handleSort = (columnKey: string) => {
    const isAsc = sortedColumn === columnKey && sortDirection === "asc";
    setSortedColumn(columnKey);
    setSortDirection(isAsc ? "desc" : "asc");
  };

  const handleInputChange = (value: any, field: string) => {
    if (field === "externalFunderId") {
      const funder = externalFunders.find((funder) => funder.externalFunderId === value);
      setEditingRow((prev: any) => ({
        ...prev,
        externalAbaNumber: funder.externalAbaNumber,
        externalAccountNumber: funder.externalAccountNumber,
        externalBankName: funder.externalBankName,
        externalAmount: funder.externalAmount,
        mcaCompany: funder.mcaCompany,
        [field]: value,
      }));
    } else {
      setEditingRow((prev: any) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleSelectRow = (rowKey: number) => {
    setSelectedRows((prevSelected: any) =>
      prevSelected.includes(rowKey)
        ? prevSelected.filter((key: any) => key !== rowKey)
        : [...prevSelected, rowKey]
    );
  };

  const handleSelectAll = () => {
    if (selectedRows.length === externalPayOffData.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(externalPayOffData.map((row: any) => row.id));
    }
  };

  const handleEditPayOffs = (row: any | null) => {
    setIsEditing(true);
    setEditingRow(row ? { ...row } : {});
  };

  const selectAllRows = (isChecked: boolean) => {
    setSelectedRows(isChecked ? externalPayOffData.map((offer: any) => offer.id) : []);
  };

  const getRowStyle = (row: any) => {
    return "regular";
  };

  const renderTotal = (key: any) => {
    let total = 0;

    if (editingRow && editingRow[key]) {
      const editingAmount = parseFloat(editingRow[key]);
      if (!isNaN(editingAmount)) {
        total += editingAmount;
      }
    }

    externalPayOffData
      .filter((row: any) => !editingRow || editingRow.id !== row.id)
      .forEach((row: any) => {
        const rowAmount = parseFloat(row[key]);
        if (!isNaN(rowAmount)) {
          total += rowAmount;
        }
      });

    return key.includes("Amount") ? formatCurrencyUSD(total.toFixed(2)) : total.toFixed(2);
  };

  const handleCancelEdit = () => {
    setEditingRow(null);
    setIsEditing(false);
  };

  const fetchExternalFunders = async () => {
    // setLoading(true);
    try {
      const tenantId = localStorage.getItem("tenantId");
      const responseData = await get(`fetchExternalFunders/${tenantId}`);
      setExternalFunders(responseData ? responseData : []);
      if (responseData) {
        const externalFundersList = responseData
          .filter((item: any) => item.mcaCompany)
          .map((item: any) => ({
            label: item.mcaCompany.toUpperCase(),
            value: item.externalFunderId,
          }))
          .sort((a: any, b: any) => {
            if (a.label > b.label) return 1;
            if (a.label < b.label) return -1;
            return 0;
          });

        setExternalFunderOptions(externalFundersList);
      }
    } catch (error) {
      console.error(error);
    } finally {
      // setLoading(false);
    }
  };

  const deletePayoff = async (rowId: any) => {
    try {
      const request = offerDetails;
      setLoading(true);
      setLoading(true);
      request.applicationId = dealApplicationId;
      request.contractTerms.externalAbaNumber = "";
      request.contractTerms.externalAccountNumber = "";
      request.contractTerms.externalAmount = 0;
      request.contractTerms.externalBankName = "";
      request.contractTerms.externalFunderId = null;
      request.contractTerms.mcaCompany = "";
      await saveFinalOffer(request);
      await offerStoreRefresh(dealApplicationId);

      await updateSyndicatorsPercentage({
        applicationId: dealApplicationId,
        reload: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const onSave = async (row: any) => {
    if (!row?.externalFunderId || !row?.externalAmount) {
      return;
    }

    try {
      const request = offerDetails;
      setLoading(true);
      request.applicationId = dealApplicationId;
      request.contractTerms.externalAbaNumber = row?.externalAbaNumber;
      request.contractTerms.externalAccountNumber = row?.externalAccountNumber;
      request.contractTerms.externalAmount = row?.externalAmount;
      request.contractTerms.externalBankName = row?.externalBankName;
      request.contractTerms.externalFunderId = row?.externalFunderId;
      request.contractTerms.mcaCompany = row?.mcaCompany;
      await saveFinalOffer(request);
      await offerStoreRefresh(dealApplicationId);

      await updateSyndicatorsPercentage({
        applicationId: dealApplicationId,
        reload: true,
      });
    } finally {
      setLoading(false);
      setEditingRow(null);
      setIsEditing(false);
    }
  };

  const handleAddPayoff = () => {
    const payOff: any = {
      externalAbaNumber: "",
      externalAccountNumber: "",
      externalAmount: "",
      externalBankName: "",
      externalFunderId: "",
      mcaCompany: "",
    };
    setEditingRow(payOff);
    setIsEditing(true);
  };

  const customButton = externalPayOffData?.length < 1 && (
    <MDButton
      color="primary"
      variant="contained"
      size="medium"
      sx={{ textTransform: "none" }}
      onClick={handleAddPayoff}
    >
      + Add Payoff
    </MDButton>
  );

  const renderTable = () => {
    return (
      <EditableTable
        rowKey={"id"}
        columns={columns}
        data={externalPayOffData}
        onDelete={deletePayoff}
        onEdit={handleEditPayOffs}
        onSave={onSave}
        onCancel={handleCancelEdit}
        editingRow={editingRow}
        isEditing={isEditing}
        onInputChange={handleInputChange}
        selectedRows={selectedRows}
        onSelectRow={handleSelectRow}
        onSelectAll={handleSelectAll}
        onSort={handleSort}
        getRowStyle={getRowStyle}
        isActionsDisabled={undefined}
        renderTotal={renderTotal}
        totalColumn={["externalAmount"]}
      />
    );
  };

  return (
    <CollapsibleCard header={"External Payoffs"} customButton={customButton}>
      <MDBox paddingRight={3}>{renderTable()}</MDBox>
    </CollapsibleCard>
  );
};

export default ExternalPayOffs;
